import { useEffect, useState } from 'react'
import { Check, Printer, Download } from 'lucide-react'
import { Button } from '../../../modules/core/index.js'
import type { PerformanceSummary } from '../types.js'
import { Modal } from '../../../modules/core/index.js'
import { SummaryDocument } from './SummaryDocument.js'
import { spanLabel } from '../labels.js'
import '../print.css'

interface PerformanceSummaryModalProps {
  summary: PerformanceSummary
  /** Saves the CSV; the dialog shows the failure if it rejects. */
  onDownload: () => Promise<void>
  backLabel?: string
  onClose: () => void
}

/** One summary, read in full, with Print (the browser's Save as PDF is the document for the employee file) and Download CSV. */
export function PerformanceSummaryModal({ summary, onDownload, backLabel, onClose }: PerformanceSummaryModalProps) {
  const [isDownloading, setIsDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The print stylesheet applies only while this dialog is open.
  useEffect(() => {
    document.body.classList.add('printing-summary')
    return () => document.body.classList.remove('printing-summary')
  }, [])

  async function download(): Promise<void> {
    setIsDownloading(true)
    setError(null)
    try {
      await onDownload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The download failed.')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Modal
      eyebrow="Performance summary"
      title={summary.worker.name}
      subtitle={spanLabel(summary.span)}
      backLabel={backLabel}
      onClose={onClose}
      footer={
        <div className="flex flex-wrap items-center gap-4">
          <Button icon={Check} onClick={onClose}>
            Close
          </Button>
          <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-1 text-xs font-bold text-marine hover:text-marine-dk">
            <Printer aria-hidden="true" className="h-3.5 w-3.5" />
            Print
          </button>
          <button type="button" onClick={() => void download()} disabled={isDownloading} className="inline-flex items-center gap-1 text-xs font-bold text-marine hover:text-marine-dk disabled:opacity-50">
            <Download aria-hidden="true" className="h-3.5 w-3.5" />
            {isDownloading ? 'Downloading…' : 'Download CSV'}
          </button>
          {error && (
            <span role="alert" className="text-xs text-coral">
              {error}
            </span>
          )}
        </div>
      }
    >
      <div className="print-area">
        <SummaryDocument summary={summary} />
      </div>
    </Modal>
  )
}
